import React, { useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Paper,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  EventRepeat as RecurringIcon,
} from "@mui/icons-material";

// Custom hooks
import { useCostData } from "./hooks/useCostData";
import { useCostHandlers } from "./hooks/useCostHandlers";

// Components
import TaxRateCard from "./components/TaxRateCard";
import ConstantCostsSummaryCard from "./components/ConstantCostsSummaryCard";
import TrainersTab from "./components/TrainersTab";
import LocationsTab from "./components/LocationsTab";
import ConstantCostsTab from "./components/ConstantCostsTab";

const CostConfigurationManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [tabValue, setTabValue] = useState(0);

  // Use custom hooks for data management
  const {
    loading,
    error,
    success,
    setError,
    setSuccess,
    showSuccessMessage,

    trainers,
    setTrainers,
    locations,
    setLocations,
    businessSettings,
    setBusinessSettings,
    constantCosts,
    constantCostsSummary,
    taxRateValue,
    setTaxRateValue,

    showInactiveConstantCosts,
    setShowInactiveConstantCosts,

    fetchData,
    getAvailableCategories,
  } = useCostData();

  // Use custom hooks for handlers
  const {
    handleUpdateTrainerCost,
    handleUpdateLocationCost,
    handleUpdateTaxRate,
    handleAddCategoryRate,
    handleUpdateCategoryRate,
    handleDeleteCategoryRate,
    handleSaveConstantCost,
    handleDeleteConstantCost,
    handleActivateConstantCost,
  } = useCostHandlers({
    trainers,
    setTrainers,
    locations,
    setLocations,
    businessSettings,
    setBusinessSettings,
    setError,
    showSuccessMessage,
    fetchData,
  });

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", p: { xs: 1, sm: 2, md: 3 } }}>
      {/* Header */}
      <Box
        sx={{
          mb: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
            Konfiguracja Kosztów
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Zarządzanie stawkami trenerów, kosztami lokalizacji, kosztami stałymi
            i podatkami
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchData}
        >
          Odśwież
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      {/* Tax Rate Card */}
      <TaxRateCard
        businessSettings={businessSettings}
        taxRateValue={taxRateValue}
        setTaxRateValue={setTaxRateValue}
        onUpdateTaxRate={handleUpdateTaxRate}
      />

      {/* Constant Costs Summary Card */}
      <ConstantCostsSummaryCard summary={constantCostsSummary} />

      {/* Tabs for Trainers, Locations, and Constant Costs */}
      <Paper sx={{ overflow: "hidden" }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          sx={{ borderBottom: 1, borderColor: "divider" }}
          variant={isMobile ? "scrollable" : "standard"}
          scrollButtons={isMobile ? "auto" : false}
        >
          <Tab
            icon={<PersonIcon />}
            iconPosition="start"
            label={`Trenerzy (${trainers.length})`}
          />
          <Tab
            icon={<LocationIcon />}
            iconPosition="start"
            label={`Lokalizacje (${locations.length})`}
          />
          <Tab
            icon={<RecurringIcon />}
            iconPosition="start"
            label={`Koszty stałe (${constantCosts.length})`}
          />
        </Tabs>

        {/* Trainers Tab */}
        {tabValue === 0 && (
          <TrainersTab
            trainers={trainers}
            isMobile={isMobile}
            getAvailableCategories={getAvailableCategories}
            onUpdateTrainerCost={handleUpdateTrainerCost}
            onAddCategoryRate={handleAddCategoryRate}
            onUpdateCategoryRate={handleUpdateCategoryRate}
            onDeleteCategoryRate={handleDeleteCategoryRate}
          />
        )}

        {/* Locations Tab */}
        {tabValue === 1 && (
          <LocationsTab
            locations={locations}
            isMobile={isMobile}
            onUpdateLocationCost={handleUpdateLocationCost}
          />
        )}

        {/* Constant Costs Tab */}
        {tabValue === 2 && (
          <ConstantCostsTab
            constantCosts={constantCosts}
            isMobile={isMobile}
            showInactive={showInactiveConstantCosts}
            setShowInactive={setShowInactiveConstantCosts}
            onSaveConstantCost={handleSaveConstantCost}
            onDeleteConstantCost={handleDeleteConstantCost}
            onActivateConstantCost={handleActivateConstantCost}
          />
        )}
      </Paper>
    </Box>
  );
};

export default CostConfigurationManagement;
